import React from "react";
import { useNavigate } from "react-router-dom";
import PortfolioNavbar from "./PortfolioNavbar";
import PortfolioFooter from "./PortfolioFooter";
import PageHero from "./PageHero";
import { Helmet } from "react-helmet-async";
import {
  Cpu,
  Database,
  Cloud,
  Terminal,
  Shield,
  Search,
  Zap,
  Layout,
  Smartphone,
  Server,
  Workflow,
  BarChart2,
  Lock,
  Layers,
  Globe,
  CheckCircle,
  ArrowRight,
} from "lucide-react";

const ToolsAndMonitoring = () => {
  const navigate = useNavigate();

  // Software Tech Stack - Frontend
  const frontendStack = [
    {
      icon: <Layout className="w-6 h-6 text-white" />,
      title: "React & Next.js",
      detail:
        "Building high-performance, SEO-friendly web applications with modern React patterns.",
    },
    {
      icon: <Smartphone className="w-6 h-6 text-white" />,
      title: "Tailwind CSS",
      detail:
        "Utility-first CSS framework for rapid UI development and consistent design systems.",
    },
    {
      icon: <Layers className="w-6 h-6 text-white" />,
      title: "TypeScript",
      detail:
        "Ensuring code reliability and maintainability with strong static typing.",
    },
    {
      icon: <Zap className="w-6 h-6 text-white" />,
      title: "Redux & Context API",
      detail:
        "Robust state management for complex application data flows.",
    },
  ];

  // Software Tech Stack - Backend & DB
  const backendStack = [
    {
      icon: <Server className="w-6 h-6 text-white" />,
      title: "Node.js & Express",
      detail:
        "Scalable server-side applications and RESTful API development.",
    },
    {
      icon: <Database className="w-6 h-6 text-white" />,
      title: "PostgreSQL & MongoDB",
      detail:
        "Expertise in both relational and NoSQL databases for optimal data storage.",
    },
    {
      icon: <Lock className="w-6 h-6 text-white" />,
      title: "Redis",
      detail:
        "High-speed in-memory data store for caching and real-time features.",
    },
    {
      icon: <Terminal className="w-6 h-6 text-white" />,
      title: "Python (FastAPI/Django)",
      detail:
        "Building high-performance APIs and data-intensive backend services.",
    },
  ];

  // Cloud & DevOps
  const devopsStack = [
    {
      icon: <Cloud className="w-6 h-6 text-white" />,
      title: "AWS & Azure",
      detail:
        "Managing scalable cloud infrastructure and serverless deployments.",
    },
    {
      icon: <Cpu className="w-6 h-6 text-white" />,
      title: "Docker & Kubernetes",
      detail:
        "Containerization and orchestration for consistent development and production environments.",
    },
    {
      icon: <Workflow className="w-6 h-6 text-white" />,
      title: "GitHub Actions",
      detail:
        "Automated CI/CD pipelines for reliable and rapid software delivery.",
    },
    {
      icon: <Shield className="w-6 h-6 text-white" />,
      title: "Terraform",
      detail:
        "Infrastructure as Code (IaC) for reproducible and version-controlled cloud setups.",
    },
  ];

  // Performance & Monitoring Tools
  const monitoringTools = [
    {
      icon: <BarChart2 className="w-6 h-6 text-orange-600" />,
      title: "Grafana & Prometheus",
      detail:
        "Real-time metrics and visualization for system performance monitoring.",
    },
    {
      icon: <Search className="w-6 h-6 text-orange-600" />,
      title: "Sentry & LogRocket",
      detail:
        "Advanced error tracking and session replay for rapid issue resolution.",
    },
    {
      icon: <Globe className="w-6 h-6 text-orange-600" />,
      title: "New Relic",
      detail:
        "Full-stack observability to monitor application health and user experience.",
    },
    {
      icon: <Terminal className="w-6 h-6 text-orange-600" />,
      title: "Jira & Confluence",
      detail:
        "Agile project management and documentation for streamlined collaboration.",
    },
  ];

  // Custom Hexagon helper (Consistent with Strategy page)
  const HexagonIcon = ({ children, color }) => (
    <div
      className={`w-14 h-14 flex items-center justify-center p-1 shadow-lg`}
      style={{
        clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
        background: color,
      }}
    >
      {children}
    </div>
  );

  return (
    <div className="relative min-h-screen bg-gray-50 overflow-hidden font-sans">
      <Helmet>
        <title>Tech Stack & Tooling | Winsil Evolution Pvt Ltd</title>
        <meta name="description" content="Explore our modern tech stack, including React, Node.js, AWS, Docker, and Kubernetes. We leverage the most reliable technologies." />
        <meta property="og:title" content="Tech Stack & Tooling | Winsil Evolution Pvt Ltd" />
        <meta property="og:description" content="Full-stack engineering with a focus on observability and performance monitoring." />
      </Helmet>
      <PortfolioNavbar />

      <PageHero
        label="Our Stack"
        labelClass="text-orange-400"
        title="Tech Stack & Tooling"
        subtitle="We leverage the most modern and reliable technologies to build scalable, secure, and high-performance software solutions."
        image="/images/photo-1558494949-ef010cbdcc31.jpg"
        imageAlt="Server infrastructure and cloud technology"
        bgClass="bg-gray-950"
        glowColor="#f97316"
      />

      {/* ── Image Gallery ── */}
      <div className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-black uppercase tracking-[0.25em] text-center mb-2 text-orange-500">Stack in Production</p>
          <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-10">Technologies We Master</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { src: "/images/photo-1607799279861-4dd421f9f5f0.jpg", alt: "Frontend development with React", label: "Frontend" },
              { src: "/images/photo-1518770660439-4636190af475.jpg", alt: "Backend infrastructure", label: "Backend & APIs" },
              { src: "/images/photo-1605810230434-7631ac76ec81.jpg", alt: "DevOps monitoring screens", label: "DevOps & CI/CD" },
            ].map((img, i) => (
              <div key={i} className="relative rounded-2xl overflow-hidden shadow-lg group h-64">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <span className="text-white font-black text-sm uppercase tracking-wider">{img.label}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
            {[
              { src: "/images/photo-1504868584819-f8e8b4b6d7e3.jpg", alt: "Performance monitoring dashboard", label: "Observability" },
              { src: "/images/photo-1544197150-b99a580bb7a8.jpg", alt: "Cloud database infrastructure", label: "Cloud & Database" },
            ].map((img, i) => (
              <div key={i} className="relative rounded-2xl overflow-hidden shadow-lg group h-56">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-orange-900/70 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <span className="text-white font-black text-sm uppercase tracking-wider">{img.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Stats Strip ── */}
      <div className="bg-gray-900 border-y border-white/10 py-12 px-4">
        <div className="max-w-5xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
          {[
            { value: "15+", label: "Languages & Frameworks" },
            { value: "3",   label: "Cloud Providers" },
            { value: "99.9%", label: "Uptime SLA" },
            { value: "24/7", label: "Monitoring Coverage" },
          ].map((s) => (
            <div key={s.label}>
              <p className="text-3xl sm:text-4xl font-black text-orange-400 mb-1">{s.value}</p>
              <p className="text-xs font-bold uppercase tracking-widest text-white/40">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto pt-12 pb-12 px-4 sm:px-6 lg:px-8 z-10">

        {/* --- 1. CORE TECHNOLOGY STACK --- */}
        <div className="bg-white p-8 rounded-xl shadow-xl border-t-4 border-orange-500 mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            
            {/* Frontend Column */}
            <div className="p-6 bg-orange-50 rounded-lg shadow-md border-l-4 border-orange-600 h-full">
              <div className="flex items-center space-x-4 mb-8">
                <HexagonIcon color="linear-gradient(135deg, #FF69B4, #8A2BE2)">
                  <Layout className="w-6 h-6 text-white" />
                </HexagonIcon>
                <h2 className="text-2xl font-bold text-orange-600">Frontend</h2>
              </div>
              <div className="space-y-6">
                {frontendStack.map((item, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="flex-shrink-0 p-2 bg-orange-600 rounded-lg h-10 w-10 flex items-center justify-center">
                      {item.icon}
                    </div>
                    <div>
                      <h4 className="text-lg font-extrabold text-gray-900 mb-1">
                        {item.title}
                      </h4>
                      <p className="text-gray-700 text-sm">{item.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Backend Column */}
            <div className="p-6 bg-orange-50 rounded-lg shadow-md border-l-4 border-orange-600 h-full">
              <div className="flex items-center space-x-4 mb-8">
                <HexagonIcon color="linear-gradient(45deg, #ff8c00, #ff4500)">
                  <Server className="w-6 h-6 text-white" />
                </HexagonIcon>
                <h2 className="text-2xl font-bold text-orange-600">Backend</h2>
              </div>
              <div className="space-y-6">
                {backendStack.map((item, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="flex-shrink-0 p-2 bg-orange-600 rounded-lg h-10 w-10 flex items-center justify-center">
                      {item.icon}
                    </div>
                    <div>
                      <h4 className="text-lg font-extrabold text-gray-900 mb-1">
                        {item.title}
                      </h4>
                      <p className="text-gray-700 text-sm">{item.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* DevOps Column */}
            <div className="p-6 bg-orange-50 rounded-lg shadow-md border-l-4 border-orange-600 h-full">
              <div className="flex items-center space-x-4 mb-8">
                <HexagonIcon color="linear-gradient(-45deg, #8b0000, #ff6347)">
                  <Cloud className="w-6 h-6 text-white" />
                </HexagonIcon>
                <h2 className="text-2xl font-bold text-orange-600">DevOps</h2>
              </div>
              <div className="space-y-6">
                {devopsStack.map((item, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="flex-shrink-0 p-2 bg-orange-600 rounded-lg h-10 w-10 flex items-center justify-center">
                      {item.icon}
                    </div>
                    <div>
                      <h4 className="text-lg font-extrabold text-gray-900 mb-1">
                        {item.title}
                      </h4>
                      <p className="text-gray-700 text-sm">{item.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>

        {/* --- 2. PERFORMANCE & MONITORING TOOLS (GRID) --- */}
        <div className="py-16 px-4 sm:px-6 lg:px-8 bg-orange-50 rounded-xl shadow-inner mb-16">
          <h2 className="text-4xl font-extrabold text-orange-600 mb-12 text-center">
            Observability & Performance Tools
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
            {monitoringTools.map((item, index) => (
              <div
                key={index}
                className="flex space-x-4 p-4 bg-white rounded-lg shadow-lg border-t-4 border-orange-500"
              >
                <div className="flex-shrink-0 p-2 border-2 border-orange-500 rounded-lg h-12">
                  {item.icon}
                </div>
                <div>
                  <h4 className="text-xl font-extrabold text-gray-900 mb-1">
                    {item.title}
                  </h4>
                  <p className="text-gray-700 text-sm">{item.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* ── Why Our Stack ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.25em] text-orange-500 mb-3">Why Our Stack</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-6">
              Tools Chosen for <span className="text-orange-500">Long-Term Performance</span>
            </h2>
            <p className="text-gray-600 text-base leading-relaxed mb-8">
              We don't chase trends. Every tool in our stack is battle-tested in production, chosen
              for reliability, community support, and the ability to scale from startup to enterprise
              without a rewrite.
            </p>
            <ul className="space-y-3 mb-10">
              {[
                "React & Next.js for blazing-fast, SEO-friendly frontends",
                "Node.js & Python for high-throughput backend services",
                "PostgreSQL + Redis for reliable, fast data persistence",
                "Docker & Kubernetes for consistent, scalable deployments",
                "Grafana & Sentry for proactive monitoring and alerting",
              ].map((item) => (
                <li key={item} className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-orange-500 flex-shrink-0" />
                  <span className="text-gray-700 text-sm font-medium">{item}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => navigate("/contact")}
              className="inline-flex items-center justify-center px-7 py-3.5 text-sm font-black uppercase tracking-wider rounded-lg bg-orange-500 text-white hover:bg-orange-400 transition-all duration-300 hover:scale-105 shadow-lg shadow-orange-500/25"
            >
              Discuss Your Tech Stack <ArrowRight className="w-4 h-4 ml-2" />
            </button>
          </div>
          <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
            <img
              src="/images/photo-1558494949-ef010cbdcc31.jpg"
              alt="Server room and cloud infrastructure"
              className="w-full h-[420px] object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-gray-900/60 via-transparent to-orange-500/10" />
            <div className="absolute bottom-5 left-5 bg-white/95 backdrop-blur-sm rounded-xl px-5 py-3 shadow-xl">
              <p className="text-2xl font-black text-orange-600">15+</p>
              <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Technologies Mastered</p>
            </div>
            <div className="absolute top-5 right-5 bg-orange-500/90 text-white rounded-full px-4 py-1.5 text-xs font-black tracking-wider shadow-lg">
              Production-Ready
            </div>
          </div>
        </div>
      </div>

      {/* ── CTA Banner ── */}
      <div className="relative bg-gray-950 py-20 px-4 overflow-hidden text-white text-center">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 to-amber-400" />
        <div className="relative max-w-3xl mx-auto">
          <p className="text-xs font-black uppercase tracking-[0.3em] text-orange-400 mb-4">Ready to Ship?</p>
          <h2 className="text-4xl sm:text-5xl font-black mb-6">Let's Build With the Right Tools</h2>
          <p className="text-white/55 text-lg mb-10 max-w-xl mx-auto">Tell us about your project and we'll recommend the ideal tech stack for your use case and scale.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate("/contact")}
              className="inline-flex items-center justify-center px-8 py-4 text-sm font-black uppercase tracking-wider rounded-lg bg-orange-500 text-white hover:bg-orange-400 transition-all duration-300 hover:scale-105 shadow-lg"
            >
              Get a Free Consultation <ArrowRight className="w-4 h-4 ml-2" />
            </button>
            <button
              onClick={() => navigate("/software-solutions")}
              className="inline-flex items-center justify-center px-8 py-4 text-sm font-bold uppercase tracking-wider rounded-lg border-2 border-white/20 text-white/70 hover:border-orange-400 hover:text-white transition-all duration-300"
            >
              View Core Solutions
            </button>
          </div>
        </div>
      </div>

      <PortfolioFooter />
    </div>
  );
};

export default ToolsAndMonitoring;
